import { createSlice, PayloadAction } from "@reduxjs/toolkit";
// Types
import Reducers from "types/Reducers";
// Async
import { getQuiz } from './quizAsync';

import jp from 'jsonpath';
import i18n from "i18n";
import IQuestion from "models/Question";
import IBucket from "models/Bucket";
import QuizTypes from "types/QuizTypes";
import IPostQuizPage from "models/PostQuizPage";
import IPage from "models/Page";
import IBanner from "models/Banner";
import ITimer from "models/Timer";
import IAnswer from "models/Answer";
import QuestionTypes from "types/QuestionTypes";

const keys = [
  'brand', 'waiver.text',
  'landingPage.buttonTitle', 'landingPage.title1', 'landingPage.title2', 'landingPage.title3',
  'callToAction.resultButtonTitle', 'callToAction.text', 'callToAction.title',
  'publicLeaderboard.headerText', 'publicLeaderboard.quizQRCodeTitle',
  'registrationPage.caption',
  'countdownPage.title', 'countdownPage.text1', 'countdownPage.text2'
];
const initialConsents:string[] = [];

interface IState {
  quiz: any;
  questions: any[];
  additionalAnswers: any[];
  answersOptions: IAnswer[];
};

const initialState:IState = {
  quiz: null,
  questions: [],
  additionalAnswers: [],
  answersOptions: []
};

const slice = createSlice({
  name: Reducers.Quiz,
  initialState,
  reducers: {
    setQuiz: (state, action:PayloadAction<any>) => {
      const { quiz, questions, answersOptions } = saveInitialTranslations(action.payload);

      i18n.changeLanguage(quiz.language);

      state.quiz = quiz;
      state.questions = questions;
      state.answersOptions = answersOptions;
    },
    updateQuizTranslations: (state, action:PayloadAction<string>) => {
      const quiz = {...state.quiz};
      const lng = action.payload;
      const defaultLanguage = quiz.language;

      quiz.consents = initialConsents;

      if (!quiz.translations[lng]) quiz.translations[lng] = {}

      keys.forEach(key => {
        jp.apply(quiz, `$.${key}`, () => quiz.translations[lng][key] || quiz.translations[defaultLanguage][key])
      })

      quiz.spotPrizes = quiz.translations[lng].spotPrizes ? [quiz.translations[lng].spotPrizes] : [quiz.translations[defaultLanguage].spotPrizes]

      quiz.prizes = quiz.prizes?.map((prize:string, index:number) => {
        const key = `prizes.${index}.prize`;
        return quiz.translations[lng][key] || quiz.translations[defaultLanguage][key]
      })

      quiz.questions?.forEach((question:IQuestion) => {
        const questionKey = `questions.${question.ref}.title`;
        const questionPath = `$.questions[?(@.ref == "${question.ref}")].title`;

        const questionCorrectAnswerDescriptionKey = `questions.${question.ref}.correctAnswerDescription`;
        const questionCorrectAnswerDescriptionPath = `$.questions[?(@.ref == "${question.ref}")].correctAnswerDescription`;
        const questionClueKey = `questions.${question.ref}.config.clue`;
        const questionCluePath = `$.questions[?(@.ref == "${question.ref}")].config.clue`;

        jp.apply(quiz, questionPath, () => quiz.translations[lng][questionKey] || quiz.translations[defaultLanguage][questionKey])
        jp.apply(quiz, questionCorrectAnswerDescriptionPath, () => quiz.translations[lng][questionCorrectAnswerDescriptionKey] || quiz.translations[defaultLanguage][questionCorrectAnswerDescriptionKey])
        jp.apply(quiz, questionCluePath, () => quiz.translations[lng][questionClueKey] || quiz.translations[defaultLanguage][questionClueKey])

        question.answers.forEach(answer => {
          const answerKey = `questions[${question.ref}].answers.${answer.ref}.title`;
          const answerPath = `$.questions[?(@.ref == "${question.ref}")].answers[?(@.ref == "${answer.ref}")].title`;
  
          jp.apply(quiz, answerPath, () => quiz.translations[lng][answerKey] || quiz.translations[defaultLanguage][answerKey])
        })

        if (question.type === QuestionTypes.ConnectionGrid) {
          question.config?.groups?.forEach((group, index) => {
            const groupKey = `questions.${question.ref}.config.groups.${index}.title`;
            const groupPath = `$.questions[?(@.ref == "${question.ref}")].config.groups.${index}.title`;

            jp.apply(quiz, groupPath, () => quiz.translations[lng][groupKey] || quiz.translations[defaultLanguage][groupKey])
          })
        }
      })

      quiz.questionsSettings?.answersOptions?.forEach((answer:IAnswer) => {
        const answerKey = `questionsSettings.answersOptions.${answer.ref}.title`;
        const answerPath = `questionsSettings.answersOptions[?(@.ref == "${answer.ref}")].title`;

        jp.apply(quiz, answerPath, () => quiz.translations[lng][answerKey] || quiz.translations[defaultLanguage][answerKey])
      })

      quiz.pages?.forEach((page:IPage) => {
        const key = `pages.${page.uuid}.title`;
        const path = `$.pages[?(@.uuid == "${page.uuid}")].title`;

        jp.apply(quiz, path, () => quiz.translations[lng][key] || quiz.translations[defaultLanguage][key])
      })

      quiz.banners?.forEach((banner:IBanner, index:number) => {
        const key = `banners.${index}.title`;
        const path = `$.banners[${index}].title`;

        jp.apply(quiz, path, () => quiz.translations[lng][key] || quiz.translations[defaultLanguage][key])
      })

      quiz.timers?.forEach((timer:ITimer, index:number) => {
        const key = `timers.${index}.title`;
        const path = `$.timers[${index}].title`;

        jp.apply(quiz, path, () => quiz.translations[lng][key] || quiz.translations[defaultLanguage][key])
      })
      
      if (quiz.type === QuizTypes.Personality) {
        quiz.postQuizPages?.forEach((postQuizPage:IPostQuizPage, index:number) => {
          const buttonTitleKey = `postQuizPages[${index}].callToAction.buttonTitle`;
          const textKey = `postQuizPages[${index}].callToAction.text`;

          jp.apply(quiz, `$.${buttonTitleKey}`, () => quiz.translations[lng][buttonTitleKey] || quiz.translations[defaultLanguage][buttonTitleKey])
          jp.apply(quiz, `$.${textKey}`, () => quiz.translations[lng][textKey] || quiz.translations[defaultLanguage][textKey])
        })
      }

      if (lng !== quiz.language) {
        quiz.consents = initialConsents.map((consentId:string) => {
          const key = `consents.${consentId}.consent`;
          return quiz.translations[lng][key] || consentId;
        })
      }

      quiz.buckets?.forEach((bucket:IBucket) => {
        const questionKey = `buckets[${bucket.ref}].title`;
        const questionPath = `$.buckets[?(@.ref == "${bucket.ref}")].title`;

        jp.apply(quiz, questionPath, () => quiz.translations[lng][questionKey] || quiz.translations[defaultLanguage][questionKey])
      })

      state.quiz = quiz;
      state.questions = quiz.questions;
      state.answersOptions = quiz.questionsSettings.answersOptions || [];
    },
    setAdditionalAnswers: (state: IState, action) => {
      state.additionalAnswers = [...state.additionalAnswers, ...action.payload];
    }
  },
  extraReducers(builder) {
    // Get quiz
    builder.addCase(getQuiz.pending, (state) => {
      state.quiz = null;
      state.questions = [];
      state.answersOptions = [];
    });
    builder.addCase(getQuiz.fulfilled, (state, action:PayloadAction<any>) => {

      const { quiz, questions, answersOptions } = saveInitialTranslations(action.payload);

      state.quiz = quiz;
      state.questions = questions;
      state.answersOptions = answersOptions;

      i18n.changeLanguage(quiz.language);
    });
    builder.addCase(getQuiz.rejected, (state) => {
      state.quiz = undefined;
      state.questions = [];
      state.answersOptions = [];
    });
  }
});

export const { setQuiz, updateQuizTranslations, setAdditionalAnswers } = slice.actions;

export default slice.reducer;

const saveInitialTranslations = (initialQuiz:any) => {
  const quiz = initialQuiz;

  quiz.translations = {
    ...(quiz.translations || {}),
    [quiz.language]: {}
  };

  keys.forEach(key => {
    quiz.translations[quiz.language] = {
      ...quiz.translations[quiz.language],
      [key]: jp.query(quiz, `$.${key}`)[0]
    }
  })

  quiz.translations[quiz.language] = {
    ...quiz.translations[quiz.language],
    spotPrizes: quiz.spotPrizes?.[0]
  }

  quiz.prizes?.forEach((prize: string, index: number) => {  
    quiz.translations[quiz.language] = {
      ...quiz.translations[quiz.language],
      [`prizes.${index}.prize`]: prize
    }
  })

  let questions = quiz.questions;

  questions?.forEach((question: IQuestion) => {  
    quiz.translations[quiz.language] = {
      ...quiz.translations[quiz.language],
      [`questions.${question.ref}.title`]: question.title,
      [`questions.${question.ref}.correctAnswerDescription`]: question.correctAnswerDescription,
      [`questions.${question.ref}.config.clue`]: question.config?.clue
    }

    question.answers?.forEach(answer => {
      quiz.translations[quiz.language] = {
        ...quiz.translations[quiz.language],
        [`questions[${question.ref}].answers.${answer.ref}.title`]: answer.title
      }
    })

    if (question.type === QuestionTypes.ConnectionGrid) {
      question.config?.groups?.forEach((group, index) => {
        quiz.translations[quiz.language] = {
          ...quiz.translations[quiz.language],
          [`questions.${question.ref}.config.groups.${index}.title`]: group.title
        }
      })
    }
  })

  const answersOptions = quiz.questionsSettings?.answersOptions || [];

  answersOptions.forEach((answer:IAnswer) => {
    quiz.translations[quiz.language] = {
      ...quiz.translations[quiz.language],
      [`questionsSettings.answersOptions.${answer.ref}.title`]: answer.title
    }
  })

  quiz.pages?.forEach((page: IPage) => {  
    quiz.translations[quiz.language] = {
      ...quiz.translations[quiz.language],
      [`pages.${page.uuid}.title`]: page.title
    }
  })

  quiz.banners?.forEach((banner: IBanner, index:number) => {  
    quiz.translations[quiz.language] = {
      ...quiz.translations[quiz.language],
      [`banners.${index}.title`]: banner.title
    }
  })

  quiz.timers?.forEach((timer: ITimer, index:number) => {  
    quiz.translations[quiz.language] = {
      ...quiz.translations[quiz.language],
      [`timers.${index}.title`]: timer.title
    }
  })
  
  if (quiz.type === QuizTypes.Personality) {
    quiz.postQuizPages?.forEach((postQuizPage:IPostQuizPage, index: number) => {
      const buttonTitleKey = `postQuizPages[${index}].callToAction.buttonTitle`;
      const textKey = `postQuizPages[${index}].callToAction.text`;

      quiz.translations[quiz.language] = {
        ...quiz.translations[quiz.language],
        [buttonTitleKey]: postQuizPage.callToAction.buttonTitle,
        [textKey]: postQuizPage.callToAction.text,
      }
    })
  }

  quiz.consents?.forEach((consentId:string) => {
    initialConsents.push(consentId)
  })

  quiz.buckets?.forEach((bucket:IBucket) => {  
    quiz.translations[quiz.language] = {
      ...quiz.translations[quiz.language],
      [`buckets[${bucket.ref}].title`]: bucket.title
    }
  })

  return { quiz, questions, answersOptions };
}
